import { ActionKeys } from '~/actions/constants';
import { call, put, takeLatest } from 'redux-saga/effects';

import * as lineItemActions from '~/actions/line-item.actions';
import * as ordersActions from '~/actions/orders.actions';
import { LINE_ITEM_FORM } from '~/constants/form';
import { setSubmitFailed, setSubmitSucceeded, startSubmit } from 'redux-form';
import * as lineItemService from '~/services/line-item.service';
import * as ordersService from '~/services/orders.service';
import { payloadToJSON } from '~/types/LineItem';
import { JSONBatchEntryExtended, batchEntryExtendedFromJSON } from '~/types/OrderBatchEntry';
import {
  trackRequestedUpdatedAxiosError,
  trackRequestedUpdatedResponse,
} from '~/utils/TrackingHelper/utils';
import { AxiosResponse } from 'axios';

export function* deleteLineItem(action: ReturnType<typeof lineItemActions.deleteLineItem>) {
  const { trackingReference } = action.payload;
  try {
    yield put(startSubmit(LINE_ITEM_FORM));

    yield call(ordersService.createBatchEntriesForOrders, [action.payload.orderObjectId]);

    yield call(lineItemService.deleteLineItem, action.payload.id, action.payload.orderObjectId);
    yield put(ordersActions.fetchOrder(action.payload.orderObjectId));
    const updatedBatchEntries: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
      ordersService.createBatchEntriesForOrders,
      [action.payload.orderObjectId],
    );

    trackRequestedUpdatedResponse(trackingReference, updatedBatchEntries);

    const parsedBatchEntries = updatedBatchEntries.data.map(batchEntryExtendedFromJSON);

    yield put(ordersActions.fetchOrderBatchEntriesSuccess(parsedBatchEntries));
    yield put(ordersActions.startBatchEntriesPolling([action.payload.orderObjectId]));

    yield put(lineItemActions.deleteLineItemSuccess());
    yield put(setSubmitSucceeded(LINE_ITEM_FORM));
  } catch (e) {
    trackRequestedUpdatedAxiosError(trackingReference, e);
    yield put(setSubmitFailed(LINE_ITEM_FORM));
    yield put(lineItemActions.deleteLineItemError());
  }
}

function* createLineItem(action: ReturnType<typeof lineItemActions.createLineItem>) {
  const { trackingReference } = action.payload;

  try {
    yield put(startSubmit(LINE_ITEM_FORM));

    yield call(ordersService.createBatchEntriesForOrders, [action.payload.orderObjectId]);

    yield call(
      lineItemService.createLineItem,
      action.payload.orderObjectId,
      payloadToJSON(action.payload.lineItemPayload),
    );

    yield put(ordersActions.fetchOrder(action.payload.orderObjectId));
    const updatedBatchEntries: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
      ordersService.createBatchEntriesForOrders,
      [action.payload.orderObjectId],
    );

    trackRequestedUpdatedResponse(trackingReference, updatedBatchEntries);

    const parsedBatchEntries = updatedBatchEntries.data.map(batchEntryExtendedFromJSON);

    yield put(ordersActions.fetchOrderBatchEntriesSuccess(parsedBatchEntries));
    yield put(ordersActions.startBatchEntriesPolling([action.payload.orderObjectId]));
    yield put(setSubmitSucceeded(LINE_ITEM_FORM));
    yield put(lineItemActions.createLineItemSuccess());
  } catch (err) {
    trackRequestedUpdatedAxiosError(trackingReference, err);
    yield put(setSubmitFailed(LINE_ITEM_FORM));
    yield put(lineItemActions.createLineItemError());
  }
}

function* editLineItem(action: ReturnType<typeof lineItemActions.editLineItem>) {
  const { trackingReference } = action.payload;
  try {
    yield put(startSubmit(LINE_ITEM_FORM));

    yield call(ordersService.createBatchEntriesForOrders, [action.payload.orderObjectId]);

    yield call(
      lineItemService.editLineItem,
      action.payload.lineItemObjectId,
      action.payload.orderObjectId,
      payloadToJSON(action.payload.lineItemPayload),
    );

    yield put(ordersActions.fetchOrder(action.payload.orderObjectId));
    const updatedBatchEntries: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
      ordersService.createBatchEntriesForOrders,
      [action.payload.orderObjectId],
    );

    trackRequestedUpdatedResponse(trackingReference, updatedBatchEntries);

    const parsedBatchEntries = updatedBatchEntries.data.map(batchEntryExtendedFromJSON);

    yield put(ordersActions.fetchOrderBatchEntriesSuccess(parsedBatchEntries));
    yield put(ordersActions.startBatchEntriesPolling([action.payload.orderObjectId]));
    yield put(lineItemActions.editLineItemSuccess());
    yield put(setSubmitSucceeded(LINE_ITEM_FORM));
  } catch (err) {
    trackRequestedUpdatedAxiosError(trackingReference, err);
    yield put(setSubmitFailed(LINE_ITEM_FORM));
    yield put(lineItemActions.editLineItemError());
  }
}

export default [
  takeLatest(ActionKeys.LINE_ITEMS_DELETE, deleteLineItem),
  takeLatest(ActionKeys.LINE_ITEM_CREATE, createLineItem),
  takeLatest(ActionKeys.LINE_ITEM_EDIT, editLineItem),
];
